import { createRouter, createWebHistory, RouterView } from "vue-router";
import { useAuthStore } from "@/stores/auth";
import { confirm } from "@/utils/confirm"; // ✅ Add this

import { auditLog, AUDIT_CODES } from "@/api/audit";
import Home from "@/views/Home.vue";
import ZoneList from "@/views/Zones_Grid.vue";
import ZoneForm from "@/views/Zone.vue";
import Weather from "@/views/Weather.vue";
import Client from "@/views/Client.vue";
import Callback from "@/views/Callback.vue"; // now a real component
import SignoutCallback from "@/views/SignoutCallback.vue";
import CreateForm from "@/views/CreateForm.vue";
import ViewForm from "@/views/ViewForm.vue";
import FormGrid from "@/views/Forms_Grid.vue";
import InspectionsGrid from "@/views/Inspections_Grid.vue";
import Inspection from "@/views/Inspection.vue";
import FindingsGrid from "@/views/Findings_Grid.vue";
import Finding from "@/views/Finding.vue";
import TasksGrid from "@/views/Tasks_Grid.vue";
import Task from "@/views/Task.vue";
import CustomerSetting from '@/views/CustomerSetting.vue';
// AuditGrid moved to Utilities - using lazy loading

import AssetsGrid from "@/views/PM/Assets_Grid.vue";
import AssetTasksGrid from "@/views/PM/AssetTasks_Grid.vue";

import IoTDashboard from "@/views/IOT/IoTDashboard.vue";
import IoTDevices   from "@/views/IOT/IoTDevices.vue";
import IoTAlerts    from "@/views/IOT/IoTAlerts.vue";
import IoTSubscriptions from "@/views/IOT/IoTSubscriptions.vue";



// ML AccessRights 13/08/2024
import { canForm } from '@/utils/permissions.js';



// ML Added formName in meta below for AccessRights 13/08/2024
const routes = [
  {
    path: "/",
    name: "Overview",
    component: Home,
    meta: { breadcrumb: "Overview", requiresAuth: true, formName: "Home" },
  },
  {
    path: "/view-map",
    name: "ViewMap",
    component: () => import("@/views/ViewMap.vue"),
    meta: { breadcrumb: "View All Map", requiresAuth: true, formName: "ViewMap" },
  },
  {
    path: "/zones",
    name: "ZoneParent",
    component: RouterView,
    meta: { breadcrumb: "Zones", requiresAuth: true },
    children: [
      {
        path: "",
        name: "ZoneList",
        component: ZoneList,
        meta: { breadcrumb: "List", requiresAuth: true, formName: "Zones_Grid" },
      },
      {
        path: "new",
        name: "ZoneCreate",
        component: ZoneForm,
        meta: { breadcrumb: "Create Zone", requiresAuth: true, formName: "Zone" },
        props: { isEdit: false },
      },
      {
        path: ":id/edit",
        name: "ZoneEdit",
        component: ZoneForm,
        meta: { breadcrumb: "Edit Zone", requiresAuth: true, formName: "Zone" },
        props: (route) => ({ isEdit: true, id: route.params.id }),
      },
    ],
  },
  {
    path: "/inspections",
    name: "InspectionParent",
    component: RouterView,
    meta: { breadcrumb: "Inspection", requiresAuth: true },
    children: [
      {
        path: "",
        name: "InspectionList",
        component: InspectionsGrid,
        meta: { breadcrumb: "List", requiresAuth: true, formName: "Inspections_Grid" },
      },
      {
        path: "new",
        name: "InspectionCreate",
        component: Inspection,
        meta: { breadcrumb: "Create Inspection", requiresAuth: true, formName: "Inspection" },
        props: { isEdit: false },
      },
      {
        path: ":id/edit",
        name: "InspectionEdit",
        component: Inspection,
        meta: { breadcrumb: "Edit Inspection", requiresAuth: true, formName: "Inspection" },
        props: (route) => ({ isEdit: false, id: route.params.id }),
      },
    ],
  },
  {
    path: "/findings",
    name: "FindingParent",
    component: RouterView,
    meta: { breadcrumb: "Finding", requiresAuth: true },
    children: [
      {
        path: "",
        name: "FindingList",
        component: FindingsGrid,
        meta: { breadcrumb: "List", requiresAuth: true, formName: "Findings_Grid" },
      },
      {
        path: "new",
        name: "FindingCreate",
        component: Finding,
        meta: { breadcrumb: "Create Finding", requiresAuth: true, formName: "Finding" },
        props: { isEdit: false },
      },
      {
        path: ":id/edit",
        name: "FindingEdit",
        component: Finding,
        meta: { breadcrumb: "Edit Finding", requiresAuth: true, formName: "Finding" },
        props: (route) => ({ isEdit: true, id: route.params.id }),
      },
    ],
  },
  {
    path: "/tasks",
    name: "TaskParent",
    component: RouterView,
    meta: { breadcrumb: "Task", requiresAuth: true },
    children: [
      {
        path: "",
        name: "TaskList",
        component: TasksGrid,
        meta: { breadcrumb: "Tasks", requiresAuth: true, formName: "Tasks_Grid" },
      },
      {
        path: "new",
        name: "TaskCreate",
        component: Task,
        meta: { breadcrumb: "Create Task", requiresAuth: true, formName: "Task" },
        props: { isEdit: false },
      },
      {
        path: ":id/edit",
        name: "TaskEdit",
        component: Task,
        meta: { breadcrumb: "Edit Task", requiresAuth: true, formName: "Task" },
        props: (route) => ({ isEdit: true, id: route.params.id }),
      },
    ],
  },
  {
    path: "/reports",
    name: "Reports",
    component: RouterView,
    meta: { breadcrumb: "Reports", requiresAuth: true },
    children: [
      {
        path: "",
        name: "ReportsList",
        component: () => import("@/views/Reports.vue"),
        meta: { breadcrumb: "Reports", requiresAuth: true, formName: "Reports" },
      },
      // AI Report Builder (re-enabled for testing; was disabled due to "AI Report Builder Failed" issue)
      { path: "ai-reports", name: "AIReports", component: () => import("@/views/AIReports.vue"), meta: { breadcrumb: "AI Report Builder", requiresAuth: true, formName: "AIReports" } },
      { path: "ai-images", name: "ImageIntelligence", component: () => import("@/views/ImageIntelligence.vue"), meta: { breadcrumb: "AI Image Review", requiresAuth: true, formName: "ImageIntelligence" } },
      { path: "monitoring-evidence", name: "MonitoringZoneEvidence", component: () => import("@/views/MonitoringZoneEvidence.vue"), meta: { breadcrumb: "Monitoring Zone Evidence", requiresAuth: true, formName: "MonitoringZoneEvidence" } },
      // 2026-05-21: Grant Evidence Pack (Phase 1) — RRO_REPORTER spec.
      { path: "evidence-pack", name: "EvidencePack", component: () => import("@/views/Reports/EvidencePack.vue"), meta: { breadcrumb: "Grant Evidence Pack", requiresAuth: true, formName: "EvidencePack" } },
      { path: "ai-dashboard", redirect: "/reports" },
      { path: "ai-dashboard/semantic-designer", redirect: "/reports" },
      { path: "ai-dashboard/widget-creator", redirect: "/reports" },
    ],
  },
  {
    path: "/team-performance",
    name: "TeamPerformance",
    component: () => import("@/views/TeamPerformance.vue"),
    meta: { breadcrumb: "Team Performance", requiresAuth: true, formName: "TeamPerformance" },
  },
  {
    path: "/sport-ireland",
    name: "SportIrelandIntegration",
    component: () => import("@/views/SportIrelandIntegration.vue"),
    meta: { breadcrumb: "Sport Ireland Integration", requiresAuth: true, formName: "SportIrelandIntegration" },
  },
  {
    path: '/assets',
  name: 'AssetGrid',
  component: () => import('@/views/PM/Assets_Grid.vue'),
},
 {
  path: "/pm/assetmap", name: "AssetsMap", component: () => import("@/views/PM/AssetMap.vue")
},
{
  path: "/pm/dashboard",
  name: "PMDashboard",
  component: () => import("@/views/PM/PMDashboard.vue"),
  meta: { breadcrumb: "PM Dashboard", requiresAuth: true, formName: "PMDashboard" },
},
{
  path: '/pm/assets/:assetId/tasks',
  name: 'AssetTasksGrid',
  component: () => import('@/views/PM/AssetTasks_Grid.vue'),
  // URL params are strings; coerce to Number so the backend JSON
  // deserializer accepts AssetId as an int in POST/PUT bodies.
  props: route => ({ assetId: Number(route.params.assetId) })
},
{
  path: '/pm/master-cycles',
  name: 'MasterCyclesGrid',
  component: () => import('@/views/PM/MasterCycles_Grid.vue'),
  meta: { breadcrumb: 'Master Cycles', requiresAuth: true, formName: 'MasterCycles_Grid' },
},
{
  path: '/pm/master-cycles/:id/assign-assets',
  name: 'MasterCycleAssignAssets',
  component: () => import('@/views/PM/MasterCycleAssignAssets.vue'),
  meta: { breadcrumb: 'Assign Assets to Days', requiresAuth: true, formName: 'MasterCycleAssignAssets' },
},
{
  path: '/cycles',
  name: 'CyclesGrid',
  component: () => import('@/views/PM/Cycles_Grid.vue'),
  props: true
},
{
  path: '/cycles/:cycleId/tasks',
  name: 'CycleTasks',
  component: () => import('@/views/PM/CycleTasks_Grid.vue'),
  props: true
},
{
  path: '/workorders',
  component: () => import('@/views/PM/WorkOrders_Grid.vue'), // or your actual Work Orders grid component
  name: 'WorkOrders'
},
{
  path: '/schedule',
  name: 'WorkOrderSchedule',
  component: () => import('@/views/PM/Schedule.vue')
},
{
  path: '/employee-loading',
  name: 'EmployeeLoadingGrid',
  component: () => import('@/views/PM/EmployeeLoading_Grid.vue'),
},
{
  path: '/pm/working-calendar',
  name: 'WorkingCalendarGrid',
  component: () => import('@/views/PM/WorkingCalendars_Grid.vue'),
  meta: { breadcrumb: "Working Calendar", requiresAuth: true, formName: "WorkingCalendars_Grid" },
},
{
  path: '/hello-test',
  name: 'HelloWorldTest',
  component: () => import('@/views/PM/HelloWorldTest.vue'),
},
{
  path: '/workorders/:workOrderId',
  name: 'WorkOrder',
  component: () => import('@/views/PM/WorkOrderPage.vue')
},
{
  path: '/workorders/:workOrderId/map',
  name: 'WorkOrderMap',
  component: () => import('@/views/PM/WorkOrderMap.vue'),
  meta: { breadcrumb: "Work Order Map", requiresAuth: true, formName: "WorkOrderMap" },
},
{
  path: '/pm/test',
  name: 'PMTest',
  component: () => import('@/views/PM/PMTest.vue'),
   props: true
},
  {
    path: "/forms",
    name: "FormParent",
    component: RouterView,
    meta: { breadcrumb: "Forms", requiresAuth: true },
    children: [
      {
        path: "",
        name: "FormList",
        component: FormGrid,
        meta: { breadcrumb: "List", requiresAuth: true, formName: "Forms_Grid" },
      },
      {
        path: "new",
        name: "FormCreate",
        component: () => import("@/views/CreateForm.vue"),
        meta: { breadcrumb: "Create Form", requiresAuth: true, formName: "CreateForm" },
        props: { isEdit: false },
      },
      {
        path: ":id/edit",
        name: "FormEdit",
        component: () => import("@/views/CreateForm.vue"),
        meta: { breadcrumb: "Edit Form", requiresAuth: true, formName: "CreateForm" },
        props: (route) => ({ isEdit: true, id: route.params.id }),
      },
      {
        path: ":id/view",
        name: "FormView",
        component: () => import("@/views/ViewForm.vue"),
        meta: { breadcrumb: "View Form", requiresAuth: true, formName: "ViewForm" },
        props: (route) => ({ id: route.params.id }),
      },
    ],
  },
  {
    path: '/customer-settings',
    name: 'CustomerSettings',
    component: CustomerSetting,
    meta: { breadcrumb: "Customer Settings", requiresAuth: true, formName: "CustomerSetting" }
  },
  {
    path: "/utilities",
    name: "UtilitiesParent",
    component: RouterView,
    meta: { breadcrumb: "Utilities", requiresAuth: true },
    children: [
      {
        path: "lookups",
        name: "LookupsGrid",
        component: () => import("@/views/Lookups_Grid.vue"),
        meta: { breadcrumb: "Lookup Types", requiresAuth: true, formName: "Lookups_Grid" },
      },
      {
        path: "lookups/types/new",
        name: "LookupTypeCreate",
        component: () => import("@/views/LookupType.vue"),
        meta: { breadcrumb: "Create Lookup Type", requiresAuth: true, formName: "LookupType" },
        props: { isEdit: false },
      },
      {
        path: "lookups/types/:id/edit",
        name: "LookupTypeEdit",
        component: () => import("@/views/LookupType.vue"),
        meta: { breadcrumb: "Edit Lookup Type", requiresAuth: true, formName: "LookupType" },
        props: (route) => ({ isEdit: true, id: route.params.id }),
      },
      {
        path: "lookups/data/:lookupType",
        name: "LookupsDataGrid",
        component: () => import("@/views/Lookups_Data_Grid.vue"),
        meta: { breadcrumb: "Lookup Values", requiresAuth: true, formName: "Lookups_Data_Grid" },
        props: (route) => ({ lookupType: route.params.lookupType }),
      },
      {
        path: "lookups/data/:lookupType/new",
        name: "LookupDataCreate",
        component: () => import("@/views/LookupData.vue"),
        meta: { breadcrumb: "Create Lookup Value", requiresAuth: true, formName: "LookupData" },
        props: (route) => ({ isEdit: false, lookupType: route.params.lookupType }),
      },
      {
        path: "lookups/data/:lookupType/:id/edit",
        name: "LookupDataEdit",
        component: () => import("@/views/LookupData.vue"),
        meta: { breadcrumb: "Edit Lookup Value", requiresAuth: true, formName: "LookupData" },
        props: (route) => ({ isEdit: true, id: route.params.id, lookupType: route.params.lookupType }),
      },
      {
        path: "map-overlays",
        name: "MapOverlaysSettings",
        component: () => import("@/views/MapOverlaysSettings.vue"),
        meta: { breadcrumb: "Map Overlays Settings", requiresAuth: true, formName: "MapOverlaysSettings" },
      },
      {
        path: "contractors",
        name: "ContractorsGrid",
        component: () => import("@/views/Contractors_Grid.vue"),
        meta: { breadcrumb: "Contractors", requiresAuth: true, formName: "Contractors_Grid" },
      },
      {
        path: "overlays",
        name: "OverlaysGrid",
        component: () => import("@/views/Overlays_Grid.vue"),
        meta: { breadcrumb: "Map Overlays", requiresAuth: true, formName: "Overlays_Grid" },
      },
      {
        path: "map-settings",
        name: "MapSettings",
        component: () => import("@/views/MapSettings.vue"),
        meta: { breadcrumb: "Customer Settings", requiresAuth: true, formName: "MapSettings" },
      },
      {
        path: "report-templates",
        name: "ReportTemplates",
        component: () => import("@/views/ReportTemplates.vue"),
        meta: { breadcrumb: "Report Templates", requiresAuth: true, formName: "ReportTemplates" },
      },
      {
        path: "email-templates",
        name: "EmailTemplates",
        component: () => import("@/views/EmailTemplates.vue"),
        meta: { breadcrumb: "Email Templates", requiresAuth: true, formName: "EmailTemplates" },
      },
      {
        path: "audit-trail",
        name: "AuditTrail",
        component: () => import("@/views/AuditGrid.vue"),
        meta: { breadcrumb: "Audit Trail", requiresAuth: true, formName: "AuditGrid" },
      },
      {
        path: "comms-log",
        name: "CommsLogGrid",
        component: () => import("@/views/CommsLog_Grid.vue"),
        meta: { breadcrumb: "Comms Log", requiresAuth: true, formName: "CommsLog_Grid" },
      },
    ],
  },
  {
    path: "/teams",
    name: "TeamParent",
    component: RouterView,
    meta: { breadcrumb: "Team", requiresAuth: true },
    children: [
      {
        path: "",
        name: "teams",
        component: () => import("@/views/TeamUserManager.vue"),
        meta: { breadcrumb: "List", requiresAuth: true, formName: "TeamUserManager" },
      },
      {
        path: "/users",
        name: "users",
        component: () => import("@/views/UserManager.vue"),
        meta: { breadcrumb: "Users", requiresAuth: true, formName: "UserManager" },
        props: { isEdit: false },
      },
    ],
  },
  {
    path: "/weather",
    name: "Weather",
    component: Weather,
    meta: { breadcrumb: "Weather", requiresAuth: true, formName: "Weather" },
  },
  {
    path: "/client",
    name: "Client",
    component: Client,
    meta: { breadcrumb: "Client", requiresAuth: true, formName: "Client" },
  },
  // Audit Trail moved to Utilities > Audit Trail

  // ----- AI Dashboards (NL → Chart) -----
  // Added 2026-05-20 as part of the Dashboards port (DashBits → WayWyser). Views existed but were orphaned.
  {
    path: "/ai-dashboards",
    name: "AIDashboard",
    component: () => import("@/views/AIDashboard/DashboardViewer.vue"),
    meta: { breadcrumb: "AI Dashboards", requiresAuth: true, formName: "AIDashboard" },
  },
  {
    path: "/ai-dashboards/widget/new",
    name: "WidgetCreator",
    component: () => import("@/views/AIDashboard/WidgetCreator.vue"),
    meta: { breadcrumb: "New Widget", requiresAuth: true, formName: "WidgetCreator" },
  },
  {
    path: "/ai-dashboards/datasets",
    name: "SemanticDatasetDesigner",
    component: () => import("@/views/AIDashboard/SemanticDatasetDesigner.vue"),
    meta: { breadcrumb: "Semantic Designer", requiresAuth: true, formName: "SemanticDatasetDesigner" },
  },
  {
    // Iframe-friendly widget preview (selection-sync target via postMessage)
    path: "/ai-dashboards/widget/preview/:id",
    name: "WidgetPreview",
    component: () => import("@/views/AIDashboard/WidgetPreview.vue"),
    props: route => ({ widgetId: Number(route.params.id) }),
    meta: { breadcrumb: "Widget Preview", requiresAuth: true, formName: "WidgetPreview" },
  },

 {
  path: "/iot",
  name: "IoTParent",
  component: RouterView,
  meta: { breadcrumb: "IoT", requiresAuth: true },
  children: [
    {
      path: "dashboard",
      name: "IoTDashboard",
      component: IoTDashboard,
      meta: { breadcrumb: "Dashboard", requiresAuth: true, formName: "IoTDashboard" },
    },
      {
        path: "devices",
        name: "IoTDevices",
        component: IoTDevices,
        meta: { breadcrumb: "Devices", requiresAuth: true, formName: "IoTDevices" },
      },
      {
        path: "waterlogger",
        name: "WaterLogger",
        component: () => import("@/views/IOT/WaterLogger.vue"),
        meta: { breadcrumb: "WaterLogger", requiresAuth: true, formName: "WaterLogger" },
      },
      {
        path: "alerts",
        name: "IoTAlerts",
        component: IoTAlerts,
        meta: { breadcrumb: "Alerts", requiresAuth: true, formName: "IoTAlerts" },
      },
      {
        path: "subscriptions",
        name: "IoTSubscriptions",
        component: IoTSubscriptions,
        meta: { breadcrumb: "Subscriptions", requiresAuth: true, formName: "IoTSubscriptions" },
      },
    {
      path: "test",
      name: "IoTTest",
      component: () => import("@/views/IOT/IoTTest.vue"),
      meta: { breadcrumb: "Test", requiresAuth: true, formName: "IoTTest" },
    },
  ],
},


  {
    path: "/reporting",
    name: "ReportingParent",
    component: RouterView,
    meta: { breadcrumb: "Reporting", requiresAuth: true },
    children: [
      {
        path: "",
        name: "ReportingHome",
        component: () => import("@/views/Reporting/ReportingHome.vue"),
        meta: { breadcrumb: "Overview", requiresAuth: true, formName: "ReportingHome" },
      },
      {
        path: "projects",
        name: "ReportingProjectParent",
        component: RouterView,
        meta: { breadcrumb: "Projects", requiresAuth: true },
        children: [
          {
            path: "",
            name: "ReportingProjectsList",
            component: () => import("@/views/Reporting/Projects_Grid.vue"),
            meta: { breadcrumb: "List", requiresAuth: true, formName: "ReportingProjects_Grid" },
          },
          {
            path: "new",
            name: "ReportingProjectCreate",
            component: () => import("@/views/Reporting/ProjectWizard.vue"),
            meta: { breadcrumb: "New Project", requiresAuth: true, formName: "ReportingProjectWizard" },
          },
          {
            path: ":id",
            name: "ReportingProjectDetail",
            component: () => import("@/views/Reporting/Project.vue"),
            meta: { breadcrumb: "Project", requiresAuth: true, formName: "ReportingProject" },
            props: (route) => ({ id: route.params.id }),
          },
        ],
      },
      {
        path: "evidence",
        name: "ReportingEvidenceReview",
        component: () => import("@/views/Reporting/EvidenceReview.vue"),
        meta: { breadcrumb: "Evidence Review", requiresAuth: true, formName: "ReportingEvidenceReview" },
      },
      {
        path: "generate",
        name: "ReportingGenerateOutput",
        component: () => import("@/views/Reporting/GenerateOutput.vue"),
        meta: { breadcrumb: "Generate Output", requiresAuth: true, formName: "ReportingGenerateOutput" },
      },
      {
        path: "submissions",
        name: "ReportingSubmissionsLog",
        component: () => import("@/views/Reporting/SubmissionsLog.vue"),
        meta: { breadcrumb: "Submissions Log", requiresAuth: true, formName: "ReportingSubmissionsLog" },
      },
      {
        path: "submissions/:id",
        name: "ReportingSubmissionDetail",
        component: () => import("@/views/Reporting/SubmissionDetail.vue"),
        meta: { breadcrumb: "Submission", requiresAuth: true, formName: "ReportingSubmissionDetail" },
        props: (route) => ({ id: route.params.id }),
      },
      {
        path: "settings",
        name: "ReportingSettings",
        component: () => import("@/views/Reporting/ReportingSettings.vue"),
        meta: { breadcrumb: "Settings", requiresAuth: true, formName: "ReportingSettings" },
      },
    ],
  },
  // ────── Citizen Science MRV (2026-05-22) ──────
  // Internal-side: issue/rotate/revoke QR-coded survey tokens, and triage the
  // verifier queue of public submissions. Public-side lives in pwa_citizen.client.
  {
    path: "/citizen-science",
    name: "CitizenScienceParent",
    component: RouterView,
    meta: { breadcrumb: "Citizen Science", requiresAuth: true },
    children: [
      {
        path: "",
        redirect: { name: "CitizenScienceQueue" },
        name: "CitizenScienceHome",
      },
      {
        path: "tokens",
        name: "CitizenScienceTokens",
        component: () => import("@/views/CitizenScience/TokensGrid.vue"),
        meta: { breadcrumb: "Survey Tokens", requiresAuth: true, formName: "CitizenScienceTokens" },
      },
      {
        path: "queue",
        name: "CitizenScienceQueue",
        component: () => import("@/views/CitizenScience/VerifierQueue.vue"),
        meta: { breadcrumb: "Verifier Queue", requiresAuth: true, formName: "CitizenScienceQueue" },
      },
    ],
  },
  {
    path: "/admin/customers",
    name: "AdminCustomers",
    component: () => import("@/views/AdminCustomers.vue"),
    meta: { breadcrumb: "System Admin", requiresAuth: true, adminOnly: true },
  },
  {
    path: "/admin/adoption",
    name: "AdminAdoption",
    component: () => import("@/views/AdminAdoption.vue"),
    meta: { breadcrumb: "Customer Adoption", requiresAuth: true, adminOnly: true },
  },
  {
    path: "/admin/version",
    name: "AdminVersion",
    component: () => import("@/views/AdminVersion.vue"),
    meta: { breadcrumb: "Version Management", requiresAuth: true, adminOnly: true },
  },
  {
    path: "/admin/mobile-rescue",
    name: "AdminMobileRescue",
    component: () => import("@/views/AdminMobileRescue.vue"),
    meta: { breadcrumb: "Mobile Rescue", requiresAuth: true, adminOnly: true },
  },
  {
    // Mobile Diagnostics moved out of the customer-facing Utilities menu into the
    // admin panel — sync/error logs are an internal support tool, not for end users.
    path: "/admin/diagnostics",
    name: "Diagnostics",
    component: () => import("@/views/Diagnostics.vue"),
    meta: { breadcrumb: "Mobile Diagnostics", requiresAuth: true, adminOnly: true },
  },
  {
    // Report Designer for the system admin: manages the global/seed templates
    // (Customer = NULL or 0) that are shared across every customer.
    path: "/admin/report-templates",
    name: "AdminReportTemplates",
    component: () => import("@/views/ReportTemplates.vue"),
    props: { adminMode: true },
    meta: { breadcrumb: "Report Designer", requiresAuth: true, adminOnly: true },
  },
  {
    // Email Designer for the system admin: manages the system/seed email templates
    // (Customer 0/NULL) that are shared across every customer.
    path: "/admin/email-templates",
    name: "AdminEmailTemplates",
    component: () => import("@/views/EmailTemplates.vue"),
    props: { adminMode: true },
    meta: { breadcrumb: "Email Designer", requiresAuth: true, adminOnly: true },
  },
  {
    // Form Designer for the system admin: manages the system/global forms
    // (Customer 0/NULL) that are shared with — and read-only to — every customer.
    // Reuses the same grid/builder views as the customer-facing /forms area, in adminMode.
    path: "/admin/forms",
    name: "AdminFormParent",
    component: RouterView,
    meta: { breadcrumb: "Form Designer", requiresAuth: true, adminOnly: true },
    children: [
      {
        path: "",
        name: "AdminFormDesigner",
        component: FormGrid,
        props: { adminMode: true },
        meta: { breadcrumb: "Form Designer", requiresAuth: true, adminOnly: true },
      },
      {
        path: "new",
        name: "AdminFormCreate",
        component: () => import("@/views/CreateForm.vue"),
        props: { isEdit: false, adminMode: true },
        meta: { breadcrumb: "Create System Form", requiresAuth: true, adminOnly: true },
      },
      {
        path: ":id/edit",
        name: "AdminFormEdit",
        component: () => import("@/views/CreateForm.vue"),
        props: (route) => ({ isEdit: true, id: route.params.id, adminMode: true }),
        meta: { breadcrumb: "Edit System Form", requiresAuth: true, adminOnly: true },
      },
      {
        path: ":id/view",
        name: "AdminFormView",
        component: () => import("@/views/ViewForm.vue"),
        props: (route) => ({ id: route.params.id, adminMode: true }),
        meta: { breadcrumb: "View System Form", requiresAuth: true, adminOnly: true },
      },
    ],
  },
  { path: "/callback", name: "Callback", component: Callback },
  { path: "/signout-callback", name: "SignoutCallback", component: SignoutCallback },
  {
    path: "/no-access",
    name: "NoAccess",
    component: () => import("@/views/NoAccess.vue"),
    meta: { breadcrumb: "Access Denied", requiresAuth: true },
  },
  { path: "/:pathMatch(.*)*", redirect: "/" },
];

const routeAuditMap = {
  Overview: { auditCode: AUDIT_CODES.DASHBOARD_ACCESSED, annotation: "Dashboard Accessed" },
  ZoneList: { auditCode: AUDIT_CODES.ZONES_LIST_VIEWED, annotation: "Zones List Viewed" },
  FormList: { auditCode: AUDIT_CODES.FORMS_LIST_VIEWED, annotation: "Forms List Viewed" },
  InspectionList: { auditCode: AUDIT_CODES.INSPECTIONS_LIST_VIEWED, annotation: "Inspections List Viewed" },
  TaskList: { auditCode: AUDIT_CODES.TASKS_LIST_VIEWED, annotation: "Tasks List Viewed" },
  FindingList: { auditCode: AUDIT_CODES.FINDINGS_LIST_VIEWED, annotation: "Findings List Viewed" },
  CustomerSettings: { auditCode: AUDIT_CODES.CUSTOMER_SETTINGS_VIEWED, annotation: "Customer Settings Viewed" },
  ContractorsGrid: { auditCode: AUDIT_CODES.CONTRACTORS_LIST_VIEWED, annotation: "Contractors List Viewed" },
  WorkOrders: { auditCode: AUDIT_CODES.WORK_ORDERS_LIST_VIEWED, annotation: "Work Orders List Viewed" },
  ReportsList: { auditCode: AUDIT_CODES.REPORTS_VIEWED, annotation: "Reports Viewed" },
  ReportTemplates: { auditCode: AUDIT_CODES.REPORT_TEMPLATES_VIEWED, annotation: "Report Templates Viewed" },
  EmailTemplates: { auditCode: AUDIT_CODES.EMAIL_TEMPLATES_VIEWED, annotation: "Email Templates Viewed" },
  MapOverlaysSettings: { auditCode: AUDIT_CODES.MAP_OVERLAYS_SETTINGS_VIEWED, annotation: "Map Overlays Settings Viewed" },
  OverlaysGrid: { auditCode: AUDIT_CODES.MAP_OVERLAYS_VIEWED, annotation: "Map Overlays Viewed" },
  Weather: { auditCode: AUDIT_CODES.WEATHER_VIEWED, annotation: "Weather Viewed" },
  AuditTrail: { auditCode: AUDIT_CODES.AUDIT_TRAIL_VIEWED, annotation: "Audit Trail Viewed" },
  teams: { auditCode: AUDIT_CODES.TEAM_USERS_VIEWED, annotation: "Team Users Viewed" },
};


const router = createRouter({
  history: createWebHistory(),
  routes,
});

// ML AccessRights 13/08/2024
//STEP 2
router.beforeEach(async (to) => {
  const auth = useAuthStore();

  // Allow OIDC callback route to pass without checks
  if (to.name === "Callback" || to.name === "SignoutCallback") {
    return true;
  }

  try {
    await auth.init();
  } catch (error) {
    console.error("[Router] Authentication initialization failed:", error);
    await auth.recoverToLogin("startup_failed", to.fullPath);
    return false;
  }

  if (to.meta.requiresAuth && !auth.isAuthenticated) {
    await auth.login(to.fullPath);
    return false;
  }

  // System admin: redirect to admin view when not already there (unless impersonating)
  if (auth.canAccessAdmin && !auth.impersonating && !to.meta.adminOnly && to.path !== "/admin/customers") {
    return { path: "/admin/customers", replace: true };
  }

  // Non-admin trying to access admin route
  if (to.meta.adminOnly && !auth.canAccessAdmin) {
    return { path: "/", replace: true };
  }

  // Enforce form-level permission. Fail-open if claims are missing/empty so a
  // brand-new login isn't locked out during the gap between auth.init() and
  // /api/AccessRights returning. Once claims arrive, normal gating applies.
  if (to.meta.formName && to.name !== "NoAccess") {
    const raw = localStorage.getItem("claims");
    let claims = null;
    try { claims = raw ? JSON.parse(raw) : null; } catch { claims = null; }
    const haveClaims = Array.isArray(claims) && claims.length > 0;
    if (haveClaims && !canForm(to.meta.formName)) {
      return { path: "/no-access", query: { form: to.meta.formName } };
    }
  }

  return true;
});

router.afterEach((to) => {
  const config = routeAuditMap[to.name];
  if (!config) return;

  window.setTimeout(() => {
    auditLog.routeViewed(config.auditCode, config.annotation).catch(() => {});
  }, 0);
});


export default router;
